import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageAutoRevive({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Revive</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Dying does not have to end a farm session. Auto Revive notices the moment your character
        goes down, gets it back up the way you chose, and the route carries on. Everything on this page is on the{' '}
        <strong className="text-white">AUTOPILOT &rsaquo; Revive</strong> tab.
      </p>

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Turn on Revive after death">
        This is the main switch in the <strong>AUTO REVIVE</strong> card. While it is on, a green dot
        shows next to <strong>AUTOPILOT</strong> in the top bar and next to <strong>Revive</strong>{' '}
        in the row of sub-tabs.
      </Step>
      <Step n={2} title="Choose how you get up">
        Pick one in <strong>Revive with</strong>. The default is <strong>Town</strong>: it is always
        free and always works. The line under the dropdown tells you what your choice costs.
      </Step>
      <Step n={3} title="That is all">
        It works with or without a running route. If a route is running, it waits while you are
        down and moves on by itself once you are back up &mdash; you do not have to press START
        again.
      </Step>

      <Shot
        name="auto-revive-01-revive-tab.png"
        tab="AUTOPILOT › Revive"
        caption="Revive with Town: three deaths, three times back up, the last one 12 minutes ago; the route is running."
        shoot="menushot (stage revive): az AUTOPILOT › Revive lap — AUTO REVIVE BE, Revive with Town és alatta a szürke „Always free...” sor; jobbra LAST DEATHS (alive, Died 3 times, Revived 3 times, Last 12 min ago, Town, Route running)."
      />

      <DocHeading>The Three Choices</DocHeading>
      <DocTable
        headers={['Revive with', 'Where you get up', 'Cost (the line under the dropdown)']}
        rows={[
          [<strong className="text-white">Where I died (costs)</strong>, 'Right where you fell, so the route loses no time.', <span className="text-amber-300">Costs andermant or an item every time. Without them you stay down.</span>],
          [<strong className="text-white">Map start</strong>, 'At the beginning of the map you died on. The route walks back from there.', <span className="text-amber-300">Needs premium or costs andermant. Without them you stay down - pick Town then.</span>],
          [<strong className="text-white">Town</strong>, 'In town. A running route teleports back by itself (see below).', <span className="text-gray-400">Always free. The route is on another map then, and waits.</span>],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Only one, and no fallback.</strong> The bot asks for your
        choice the moment you die and keeps asking, a little over a second apart, until you are
        standing again. If that way does not work for you, it does not switch to another one &mdash;
        the character stays down. That is why the card spells out the cost, and why Town is the
        default. If an older version had several ticked, it keeps Town (or the first one if Town was
        not among them).
      </p>

      <DocHeading>Messages on the Auto Revive Card</DocHeading>
      <DocTable
        headers={['Message', 'What it means']}
        rows={[
          [<span className="text-amber-300">Nothing is chosen - nobody gets up.</span>, 'The switch is on, but no way to get up is chosen. Pick one.'],
          [<span className="text-red-400">Disabled: unexpected message layout.</span>, 'The bot noticed something it did not expect (usually after a game update) and switched reviving off to stay safe. Get up by hand until a bot update fixes it.'],
        ]}
      />

      <DocHeading>The Last Deaths Card</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This card answers the question you open the tab for: <em>did it get me up, and how?</em>
      </p>
      <DocTable
        headers={['Line', 'What it shows']}
        rows={[
          [<strong className="text-white">State</strong>, <><span className="text-emerald-400">alive</span> or <span className="text-red-400">down</span>, live.</>],
          [<strong className="text-white">Died</strong>, 'How many times you died since the bot was loaded, or "not yet".'],
          [<strong className="text-white">Revived</strong>, 'How many times you got back up. It turns orange while it is still 0 after a death.'],
          [<strong className="text-white">Last</strong>, 'When you last got up ("40 s ago", "12 min ago") and the option the bot asked for last. The game does not say which request actually worked, so treat it as the most likely one.'],
          [<strong className="text-white">Route</strong>, <><strong>running</strong>, <strong>held</strong> with the milliseconds left while the route is paused for you, or <strong>not running</strong>.</>],
        ]}
      />

      <DocHeading>Getting Up in Town</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        When you get up anywhere on the route, you see a short <strong>Back on your feet.</strong>{' '}
        notice and the run simply goes on. Town is different: your route belongs to other maps. If
        a route is running, it goes back in by itself, the same way a START from town does, and you
        see <strong>Up in town - teleporting back to the route.</strong> This works for every route
        that can start from town &mdash; the Play tab shows <em>elsewhere - it teleports in</em> for
        those.
      </p>
      <Callout type="warning">
        <strong>&ldquo;Up in town - this route cannot teleport back by itself. Go back or restart
        it.&rdquo;</strong> The route has no way in from town. Walk or teleport back to a map of the
        route and it picks itself up, or press STOP and START on{' '}
        <button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Play</button>.
      </Callout>

      <DocHeading>Reviving in a Party</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        If <strong>Play as a party</strong> and <strong>Accept resurrect offers</strong> are both on
        on{' '}
        <button onClick={() => navigate('party')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Party</button>,
        a teammate&rsquo;s resurrection is the better way up: the team stays together and it costs
        you nothing. So when a living teammate who has <strong>Resurrect dead teammates</strong>{' '}
        turned on stands close to you (within the game&rsquo;s 20 m resurrect range, in the same
        map instance), your own revive holds back for up to about 6 seconds to give that offer
        time to arrive. Once an offer is being accepted, it keeps waiting. If no offer comes, your
        own choice takes over as usual. With nobody in range it does not wait at all.
      </p>
      <Callout type="info">
        Turn Revive after death on in every client of the team. A teammate who is down but getting
        up is waited for; one who will never get up is left behind once the party&rsquo;s patience
        runs out.
      </Callout>

      <DocHeading>Why Doesn&rsquo;t It Work?</DocHeading>
      <DocTable
        headers={['What you see', 'What to do']}
        rows={[
          ['Died goes up, Revived stays at 0 (orange)', 'Check the card: either nothing is chosen, or the red "Disabled" line is showing. With Map start or Where I died chosen, you stay down without premium or andermant - pick Town.'],
          ['You got up, but the route is not moving', 'You were revived in town and the route cannot teleport back by itself. See Getting Up in Town above.'],
          ['It spent andermant', 'Where I died (costs) or Map start is chosen. Pick Town.'],
          ['Combat stopped while you were down', 'Normal. Fighting and walking pause while you are dead and start again when you are up.'],
        ]}
      />
    </div>
  );
}

export default PageAutoRevive;
